import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../../firebase/config';
import {
  Inbox,
  CheckCircle2,
  XCircle,
  Eye,
  AlertTriangle,
  Clock,
  ShieldAlert,
  Calendar,
} from 'lucide-react';
import type { Contribution, Member } from '../../../../../shared/types';

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost/fhpf-api';

export const PendingQueue: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [pendingList, setPendingList] = useState<Contribution[]>([]);
  const [membersMap, setMembersMap] = useState<Record<string, Member>>({});
  const [loading, setLoading] = useState(true);

  // Modals & Action States
  const [selectedProofUrl, setSelectedProofUrl] = useState<string | null>(null);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [actionInProgress, setActionInProgress] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Subscribe to pending contributions
  useEffect(() => {
    const q = query(collection(db, 'contributions'), where('status', '==', 'pending'));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list: Contribution[] = [];
        snap.forEach((doc) => list.push(doc.data() as Contribution));
        setPendingList(list);
        setLoading(false);
      },
      (err) => {
        console.warn('[FHPF] Queue load error:', err.message);
        setLoading(false);
      }
    );

    // Also load member names
    const unsubMembers = onSnapshot(collection(db, 'members'), (snap) => {
      const map: Record<string, Member> = {};
      snap.forEach((doc) => {
        map[doc.id] = doc.data() as Member;
      });
      setMembersMap(map);
    });

    return () => {
      unsub();
      unsubMembers();
    };
  }, []);

  const handleApprove = async (contribution: Contribution) => {
    if (!user) return;
    if (contribution.memberId === user.uid) {
      alert(t('admin.selfApprovalForbidden'));
      return;
    }

    if (!confirm(`${t('admin.confirmApprove')}: ${contribution.contributionId}?\n\n${t('admin.confirmApproveMsg')}`)) {
      return;
    }

    setActionInProgress(true);
    setMessage(null);

    try {
      const token = await user.getIdToken();
      const res = await fetch(`${API_BASE}/contributions/${contribution.contributionId}/approve`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error?.message || 'Approval failed.');
      }

      setMessage({ type: 'success', text: `Contribution approved. Recorded at Ledger sequence #${data.ledgerSequence}.` });
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Approval failed.' });
    } finally {
      setActionInProgress(false);
    }
  };

  const handleReject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !rejectingId) return;

    if (rejectionReason.trim().length < 5) {
      alert('Please provide a reason with at least 5 characters.');
      return;
    }

    setActionInProgress(true);
    setMessage(null);

    try {
      const token = await user.getIdToken();
      const res = await fetch(`${API_BASE}/contributions/${rejectingId}/reject`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ rejectionReason: rejectionReason.trim() }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error?.message || 'Rejection failed.');
      }

      setMessage({ type: 'success', text: 'Contribution rejected and member notified.' });
      setRejectingId(null);
      setRejectionReason('');
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Rejection failed.' });
    } finally {
      setActionInProgress(false);
    }
  };

  const fmt = (n: number) => `৳${n.toLocaleString('en-US')}`;

  return (
    <div className="space-y-6">
      {/* Notifications */}
      {message && (
        <div
          className={`p-4 rounded-xl border flex items-start gap-2.5 text-xs sm:text-sm ${
            message.type === 'success'
              ? 'bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900'
              : 'bg-red-50 text-red-800 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-900'
          }`}
        >
          {message.type === 'success' ? (
            <CheckCircle2 className="w-5 h-5 shrink-0 text-emerald-600 mt-0.5" />
          ) : (
            <AlertTriangle className="w-5 h-5 shrink-0 text-red-600 mt-0.5" />
          )}
          <span>{message.text}</span>
        </div>
      )}

      {/* Queue List */}
      <div className="bg-white dark:bg-navy-900 rounded-2xl border border-gray-200/80 dark:border-navy-800 shadow-sm overflow-hidden">
        <div className="p-4 sm:p-5 border-b border-gray-100 dark:border-navy-800 flex items-center justify-between">
          <h2 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Inbox className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            <span>{t('admin.pendingQueue')}</span>
          </h2>
          <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300">
            {pendingList.length} Pending
          </span>
        </div>

        {loading ? (
          <div className="py-12 text-center text-sm text-gray-400">{t('common.loading')}</div>
        ) : pendingList.length === 0 ? (
          <div className="py-12 text-center text-sm text-gray-400">
            All caught up! No pending contributions in the queue.
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-navy-800">
            {pendingList.map((c) => {
              const memberName = membersMap[c.memberId]?.name || c.memberId;
              const isSelf = c.memberId === user?.uid;

              return (
                <div key={c.contributionId} className="p-4 sm:p-5 hover:bg-gray-50/50 dark:hover:bg-navy-800/40 transition-colors">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-base text-gray-900 dark:text-white">
                          {memberName}
                        </span>
                        <span className="text-xs font-mono px-2 py-0.5 rounded bg-gray-100 dark:bg-navy-800 text-gray-600 dark:text-gray-300">
                          {c.month}
                        </span>
                        {c.attemptNo > 1 && (
                          <span className="text-xs font-semibold text-amber-600 bg-amber-50 dark:bg-amber-950/40 px-2 py-0.5 rounded">
                            Attempt #{c.attemptNo}
                          </span>
                        )}
                      </div>

                      <div className="text-xs text-gray-400 flex items-center gap-3">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          {c.submittedInDhakaTime || c.submittedAt}
                        </span>
                        <span className="font-mono text-[10px]">Proof: {c.proofSha256?.substring(0, 12)}...</span>
                      </div>

                      {/* Self-approval warning banner */}
                      {isSelf && (
                        <div className="flex items-center gap-1.5 text-xs text-red-600 dark:text-red-400 font-medium pt-1">
                          <ShieldAlert className="w-4 h-4 shrink-0" />
                          <span>{t('admin.selfApprovalForbidden')}</span>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center justify-between md:justify-end gap-3 sm:gap-4">
                      <div className="text-right">
                        <div className="font-bold text-base text-gray-900 dark:text-white">
                          {fmt(c.totalAmount || c.amount + (c.lateFee || 0))}
                        </div>
                        <div className="text-xs text-gray-400">
                          {fmt(c.amount)} {c.lateFee > 0 ? `+ ${fmt(c.lateFee)} late` : '(On Time)'}
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        {/* View Proof Button */}
                        <button
                          type="button"
                          onClick={() => setSelectedProofUrl(`${API_BASE}/proofs/${c.proofId}.jpg/stream`)}
                          className="p-2 rounded-xl border border-gray-200 dark:border-navy-700 hover:bg-gray-100 dark:hover:bg-navy-800 text-gray-700 dark:text-gray-200 text-xs font-medium flex items-center gap-1"
                          title="View Deposit Screenshot"
                        >
                          <Eye className="w-4 h-4 text-primary-500" />
                          <span className="hidden sm:inline">Receipt</span>
                        </button>

                        {/* Approve Button */}
                        <button
                          type="button"
                          disabled={actionInProgress || isSelf}
                          onClick={() => handleApprove(c)}
                          className="px-3.5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold flex items-center gap-1.5 shadow-sm shadow-emerald-600/20 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <CheckCircle2 className="w-4 h-4" />
                          <span>{t('admin.approveBtn')}</span>
                        </button>

                        {/* Reject Button */}
                        <button
                          type="button"
                          disabled={actionInProgress}
                          onClick={() => setRejectingId(c.contributionId)}
                          className="px-3 py-2 rounded-xl bg-red-600 hover:bg-red-700 text-white text-xs font-semibold flex items-center gap-1.5 shadow-sm shadow-red-600/20 disabled:opacity-50"
                        >
                          <XCircle className="w-4 h-4" />
                          <span>{t('admin.rejectBtn')}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Rejection Modal */}
      {rejectingId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy-950/70 backdrop-blur-sm">
          <div className="bg-white dark:bg-navy-900 rounded-2xl max-w-md w-full p-6 shadow-2xl border border-gray-200 dark:border-navy-800">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
              Reject Contribution: {rejectingId}
            </h3>
            <p className="text-xs text-gray-500 mb-4">
              State clearly why this payment is being rejected. The member will be able to read this note and resubmit their deposit.
            </p>

            <form onSubmit={handleReject} className="space-y-4">
              <textarea
                required
                rows={3}
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder={t('admin.rejectionReasonPlaceholder')}
                className="w-full p-3 text-sm rounded-xl border border-gray-300 dark:border-navy-700 bg-gray-50 dark:bg-navy-950 outline-none focus:ring-2 focus:ring-red-500 text-gray-900 dark:text-white"
              />

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setRejectingId(null)}
                  className="px-4 py-2 text-xs font-semibold rounded-xl border border-gray-200 dark:border-navy-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-navy-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={actionInProgress}
                  className="px-4 py-2 text-xs font-semibold rounded-xl bg-red-600 hover:bg-red-700 text-white"
                >
                  Confirm Rejection
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Proof Viewer Modal */}
      {selectedProofUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy-950/80 backdrop-blur-sm">
          <div className="bg-white dark:bg-navy-900 rounded-2xl max-w-xl w-full p-4 shadow-2xl border border-gray-200 dark:border-navy-800 space-y-3">
            <div className="flex items-center justify-between">
              <span className="font-bold text-sm text-gray-900 dark:text-white">Deposit Slip Screenshot</span>
              <button
                type="button"
                onClick={() => setSelectedProofUrl(null)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-sm font-semibold p-1"
              >
                ✕ Close
              </button>
            </div>
            <div className="bg-black/5 dark:bg-black/40 rounded-xl p-2 flex items-center justify-center min-h-[300px]">
              <img
                src={selectedProofUrl}
                alt="Deposit Slip"
                className="max-h-[60vh] object-contain rounded-lg"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
